import { useEffect, useState, useCallback, useMemo } from "react";
import { MuiMaterials } from '../@index.js';
import * as global from '../data/global';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import { outlinedActionButtonSx, withMenuScrollHintTheme } from './windowChrome/index.js';

const SensorTemperatureGraphFilter = ({ filterOptions, setFilterOptions }) => {
    let fd = new Date();
    fd.setDate(fd.getDate() - 5);
    const initialBins = (global.gw.selectedSite?.data?.bins || []).map((b) => ({
        binName: b.name,
        binId: b.binId,
        cableList: (b.cableList || []).map((c) => ({
            binId: b.binId,
            cableId: c.id,
            cableName: c.name,
            sensorList: (c.sensorList || []).map((s) => ({
                binId: b.binId,
                cableId: s.cableId,
                sensorId: s.id,
                sensorName: s.name,
            })),
        })),
    }));
    const [binIds, setBinIds] = useState(initialBins);
    const [cableIds, setCableIds] = useState(initialBins.flatMap((b) => b.cableList));
    const sensorIds = cableIds.flatMap((c) => c.sensorList);
    const [selectedBinId, setSelectedBinId] = useState([initialBins[0]?.binId ?? 0]);
    const [selectedCableId, setSelectedCableId] = useState([
        initialBins[0]?.cableList?.[0]?.cableId ?? 0,
    ]);
    const [selectedSensorId, setSelectedSensorId] = useState([0]);
    const [dateFrom, setDateFrom] = useState(fd);
    const [dateTo, setDateTo] = useState(new Date());
    const [hourFrom, setHourFrom] = useState(0);
    const [hourTo, setHourTo] = useState(24);
    const [isModalOpen, setIsModalOpen] = useState(null);
    const [forceUpdate, setForceUpdate] = useState(0);

    const [cables, setCables] = useState(null);
    const [sensors, setSensors] = useState(null);

    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    const theme = useMemo(() => {
        const isLight = global.gw.theme === 'light';
        return MuiMaterials.createTheme({
    components: withMenuScrollHintTheme({
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: isLight ? '#000000' : '#f4cc8e',
                    fontSize: "18px",
                    borderColor: isLight ? '#666666' : 'rgb(244, 204, 142)',
                    ...(isLight && {
                        '&:not(.MuiInputLabel-shrink)': {
                            top: '50%',
                            transform: 'translateY(-50%)',
                            left: '14px',
                        },
                    }),
                },
                shrink: {
                    top: isLight ? '8px' : undefined,
                },
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: "18px",
                    background: isLight ? '#e0e0e0' : '#2d2d2d',
                    color: isLight ? 'rgb(51, 51, 51)' : 'rgb(244, 204, 142)',
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                sizeMedium: {
                    color: isLight ? 'rgb(51, 51, 51)' : 'rgb(244, 204, 142)'
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    color: isLight ? '#333333' : '#f4cc8e',
                    "& .MuiOutlinedInput-notchedOutline": {
                        border: isLight ? '0.5px solid #cccccc' : '0.5px solid #f4cc8e4d',
                    },
                    "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: isLight ? '0.5px solid #666666' : '0.5px solid #f4cc8e',
                        },
                    },
                    "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: isLight ? '0.5px solid #666666' : '0.5px solid #f4cc8e',
                        },
                    },
                }
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    ".MuiInputLabel-root.Mui-focused": {
                        color: isLight ? '#000000' : '#f4cc8e',
                    },
                },
            }
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    border: isLight ? '0.5px solid #cccccc' : '0.5px solid #f4cc8e4d',
                    ".MuiInputLabel-root.Mui-focused": {
                        color: isLight ? '#000000' : '#f4cc8e',
                    },
                    ".MuiSvgIcon-root": {
                        color: isLight ? '#333333' : '#f4cc8e',
                    },
                },
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: isLight ? '#333333' : '#f4cc8e',
                },
            }
        },
    }),
    });
    }, [forceUpdate]);

    useEffect(() => {
        let tempSelectedBinId = selectedBinId;
        tempSelectedBinId[0] = selectedBinId[0];
        setSelectedBinId(tempSelectedBinId);

        renderCables(selectedBinId[0]);

        let tempSelectedCableId = selectedCableId;
        tempSelectedCableId[0] = cableIds[0].cableId;
        setSelectedCableId(tempSelectedCableId);

        renderSensors(selectedBinId[0], selectedCableId[0]);

        setFilterOptions({
            binId: selectedBinId[0],
            binName: binIds.filter(b => b.binId === selectedBinId[0])[0]?.binName,
            cableId: selectedCableId[0],
            cableName: selectedCableId[0] == 0 ? "All" : cableIds.filter(c => c.cableId === selectedCableId[0])[0].cableName,
            sensorId: selectedSensorId[0],
            sensorName: selectedSensorId[0] == 0 ? "All" : sensorIds.filter(s => s.sensorId == selectedSensorId[0])[0].sensorName,
            dateFrom: dateFrom,
            dateTo: dateTo,
            hourFrom: hourFrom,
            hourTo: hourTo,
        });
    }, []);

    const handleBinChange = (event) => {
        let tempSelectedBinId = selectedBinId;
        tempSelectedBinId[0] = event.target.value;
        setSelectedBinId(tempSelectedBinId);

        renderCables(event.target.value);

        let tempSelectedCableId = selectedCableId;
        tempSelectedCableId[0] = cableIds.filter(c => c.binId == event.target.value)[0].cableId;
        setSelectedCableId(tempSelectedCableId);

        renderSensors(event.target.value);

        let tempSelectedSensorId = selectedSensorId;
        tempSelectedSensorId[0] = 0;
        setSelectedSensorId(tempSelectedSensorId);
    };

    const handleCableChange = (event) => {
        let tempSelectedCableId = selectedCableId;
        tempSelectedCableId[0] = event.target.value;
        setSelectedCableId(tempSelectedCableId);

        binIds.map((b, i) => {
            let cableFilter = b.cableList.filter(c => c.cableId === event.target.value);
            if (cableFilter && cableFilter.length > 0) {
                renderSensors(b.binId, cableFilter[0].cableId);

                let tempSelectedSensorId = selectedSensorId;
                tempSelectedSensorId[0] = 0;
                setSelectedSensorId(tempSelectedSensorId);
            }
        });
    };

    const handleSensorChange = (event) => {
        let tempSelectedSensorId = selectedSensorId;
        tempSelectedSensorId[0] = event.target.value;
        setSelectedSensorId(tempSelectedSensorId);
        //var sensorFilter = sensorIds.filter(s => s.sensorId == selectedSensorId[0]);
        //var sensorName = sensorFilter[0].sensorName;
    };

    const renderBins = () => {
        return (binIds.map((b, i) => (
            <MuiMaterials.MenuItem value={b.binId}>{b.binName}</MuiMaterials.MenuItem>
        )));
    };

    const renderCables = (binId) => {
        let bins = binIds.filter(b => b.binId === binId);
        if (bins && bins.length > 0) {
            let cableList = bins[0].cableList;
            if (cableList && cableList.length > 0) {
                if (cableList[0].cableId !== 0) {
                    cableList.splice(0, 0, { cableId: 0, cableName: "All" });
                }
                setCables(cableList.map((c, i) => (
                    <MuiMaterials.MenuItem value={c.cableId}>{c.cableName}</MuiMaterials.MenuItem>
                )));
            }
        }
    };

    const renderSensors = (binId, cableId) => {
        let bins = binIds.filter(b => b.binId === binId);
        if (bins && bins.length > 0) {
            let cableList = bins[0].cableList;
            if (cableId) {
                cableList = cableList.filter(c => c.cableId === cableId);
            }
            if (cableList && cableList.length > 0) {
                let sensorList = [{ sensorId: 0, sensorName: "All" }];
                cableList.map((c, i) => {
                    if (c.sensorList) {
                        c.sensorList.map((s, i) => {
                            sensorList.push(s);
                        })
                    }
                });
                if (sensorList && sensorList.length > 0) {
                    setSensors(sensorList.map((s, i) => (
                        <MuiMaterials.MenuItem value={s.sensorId}>{s.sensorName}</MuiMaterials.MenuItem>
                    )));
                }
            }
        }
    };

    const renderModal = () => {
        return (   
            <MuiMaterials.Modal
                key={`history-filter-modal-${global.gw.theme}-${forceUpdate}`}
                open={isModalOpen}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                sx={{
                    backgroundColor: getThemeColor("rgba(255, 255, 255, 0.45)", "rgba(0, 0, 0, 0.45)"),
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <MuiMaterials.Box
                    sx={{
                        background: getThemeColor('#ffffff', '#121212'),
                        margin: "auto 4px",
                        textAlign: "center",
                    }}
                >
                    <MuiMaterials.Button
                        sx={{
                            color: getThemeColor('#333333', '#f4cc8e'),
                            margin: "auto",
                            maxWidth: "40px",
                            maxHeight: "40px",
                            minWidth: "40px",
                            minHeight: "40px",
                            position: "absolute",
                            right: "2px",
                            top: "0",
                            '&:hover': {
                                backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                            },
                        }}
                        onClick={e => {
                            setIsModalOpen(false);
                        }}
                    >
                        <CloseIcon />
                    </MuiMaterials.Button>
                    <MuiMaterials.Box
                        sx={{
                            //height: "100%",
                            //margin: "14px",
                            //overflowX: "auto",
                            marginTop: "10px",
                            marginBottom: "10px",
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            justifyContent: "center",
                        }}
                    >
                        <MuiMaterials.ThemeProvider theme={theme} key={`history-filter-theme-${global.gw.theme}-${forceUpdate}`}>
                            <MuiMaterials.FormControl
                                sx={{
                                    width: "160px",
                                    margin: "10px 10px 10px 0px",
                                }}
                            >
                                <MuiMaterials.TextField
                                    select
                                    value={selectedBinId}
                                    label="Bin"
                                    onChange={handleBinChange}
                                >
                                    {renderBins()}
                                </MuiMaterials.TextField>
                            </MuiMaterials.FormControl>
                            <MuiMaterials.FormControl
                                sx={{
                                    width: "160px",
                                    margin: "10px 10px 10px 0px",
                                }}
                            >
                                <MuiMaterials.TextField
                                    select
                                    value={selectedCableId}
                                    label="Cable"
                                    onChange={handleCableChange}
                                >
                                    {cables}
                                </MuiMaterials.TextField>
                            </MuiMaterials.FormControl>
                            <MuiMaterials.FormControl
                                sx={{
                                    width: "160px",
                                    margin: "10px 10px 10px 0px",
                                }}
                            >
                                <MuiMaterials.TextField
                                    select
                                    value={selectedSensorId}
                                    label="Sensor"
                                    onChange={handleSensorChange}
                                >
                                    {sensors}
                                </MuiMaterials.TextField>
                            </MuiMaterials.FormControl>
                            <MuiMaterials.LocalizationProvider
                                dateAdapter={MuiMaterials.AdapterDateFns}
                            >
                                <MuiMaterials.DatePicker
                                    label="Date From"
                                    className="graphDateFrom"
                                    value={dateFrom}
                                    sx={{
                                        width: "160px",
                                        margin: "10px 10px 10px 0px",
                                    }}
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                ".MuiDayCalendar-weekDayLabel": {
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiPaper-root": {
                                                    border: `1px solid ${getThemeColor('#cccccc', '#f4cc8e')}`,
                                                    borderRadius: "10px",
                                                    background: getThemeColor('#ffffff', '#2d2d2d'),
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiButtonBase-root": {
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiPickersDay-today": {
                                                    borderColor: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiPickersDay-root.Mui-selected": {
                                                    background: getThemeColor('#cccccc', 'black'),
                                                    ':hover': {
                                                        bgcolor: getThemeColor('#cccccc', 'black'),
                                                    },
                                                    ':active': {
                                                        bgcolor: getThemeColor('#cccccc', 'black'),
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    onChange={setDateFrom}
                                />
                                <MuiMaterials.DatePicker
                                    label="Date To"
                                    className="graphDateTo"
                                    value={dateTo}
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                ".MuiDayCalendar-weekDayLabel": {
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiPaper-root": {
                                                    border: `1px solid ${getThemeColor('#cccccc', '#f4cc8e')}`,
                                                    borderRadius: "10px",
                                                    background: getThemeColor('#ffffff', '#2d2d2d'),
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiButtonBase-root": {
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiPickersDay-today": {
                                                    borderColor: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiPickersDay-root.Mui-selected": {
                                                    background: getThemeColor('#cccccc', 'black'),
                                                    ':hover': {
                                                        bgcolor: getThemeColor('#cccccc', 'black'),
                                                    },
                                                    ':active': {
                                                        bgcolor: getThemeColor('#cccccc', 'black'),
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        width: "160px",
                                        margin: "10px 10px 10px 0px",
                                    }}
                                    onChange={setDateTo}
                                />
                            </MuiMaterials.LocalizationProvider>
                            <MuiMaterials.TextField
                                className="graphHourFrom"
                                label="Hour From"
                                sx={{
                                    //margin: "2px 0px",
                                    //marginLeft: "-5px",
                                    width: "95px",
                                    margin: "10px 10px 10px 0px",
                                }}
                                InputProps={{
                                    style: {
                                        fontSize: "18px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                    }
                                }}
                                inputProps={{
                                    style: {
                                        padding: "15px 5px",
                                        textAlign: "center",
                                    }
                                }}
                                defaultValue={hourFrom}
                                onFocus={() => {
                                    let graphHourFrom = window.document.querySelector('.graphHourFrom .MuiInputBase-input');
                                    if (graphHourFrom) {
                                        graphHourFrom.select();
                                    }
                                }}
                                onChange={(e) => {
                                    setHourFrom(+e.target.value);
                                }}
                                type="number"
                                min="1"
                                max="24"
                                inputmode="numeric"
                            />
                            <MuiMaterials.TextField
                                className="graphHourTo"
                                label="Hour To"
                                sx={{
                                    //margin: "2px 0px",
                                    //marginLeft: "10px",
                                    margin: "10px 0px",
                                    width: "95px",
                                }}
                                InputProps={{
                                    style: {
                                        fontSize: "18px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                    }
                                }}
                                inputProps={{
                                    style: {
                                        padding: "15px 5px",
                                        textAlign: "center",
                                    }
                                }}
                                defaultValue={hourTo}
                                onFocus={() => {
                                    let graphHourTo = window.document.querySelector('.graphHourTo .MuiInputBase-input');
                                    if (graphHourTo) {
                                        graphHourTo.select();
                                    }
                                }}
                                onChange={(e) => {
                                    setHourTo(+e.target.value);
                                }}
                                type="number"
                            />
                        </MuiMaterials.ThemeProvider>
                    </MuiMaterials.Box>
                    <MuiMaterials.Box
                        sx={{
                            display: "flex",
                            gap: "10px",
                            justifyContent: "center",
                            marginBottom: "18px",
                        }}
                    >
                        <MuiMaterials.Button
                            key={`history-filter-cancel-${global.gw.theme}-${forceUpdate}`}
                            size="small"
                            variant="outlined"
                            sx={{ width: "114px", ...outlinedActionButtonSx() }}
                            onClick={() => {
                                setIsModalOpen(false);
                            }}
                        >
                            Cancel
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            key={`history-filter-apply-${global.gw.theme}-${forceUpdate}`}
                            size="small"
                            variant="outlined"
                            sx={{ width: "114px", ...outlinedActionButtonSx() }}
                            onClick={async () => {
                                setIsModalOpen(false);

                            setFilterOptions({
                                binId: selectedBinId[0],
                                binName: binIds.filter(b => b.binId === selectedBinId[0])[0]?.binName,
                                cableId: selectedCableId[0],
                                cableName: selectedCableId[0] == 0 ? "All" : cableIds.filter(c => c.cableId === selectedCableId[0])[0].cableName,
                                sensorId: selectedSensorId[0],
                                sensorName: selectedSensorId[0] == 0 ? "All" : sensorIds.filter(s => s.sensorId == selectedSensorId[0])[0].sensorName,
                                dateFrom: dateFrom,
                                dateTo: dateTo,
                                hourFrom: hourFrom,
                                hourTo: hourTo,
                            });
                        }}
                    >
                        Apply
                    </MuiMaterials.Button>
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
            </MuiMaterials.Modal>
        );
    };

    return (
        <>
            <MuiMaterials.Button
                key={`history-filter-button-${global.gw.theme}-${forceUpdate}`}
                sx={{
                    color: getThemeColor('#333333', '#f4cc8e'),
                    maxWidth: "40px",
                    maxHeight: "40px",
                    minWidth: "40px",
                    minHeight: "40px",
                    flexShrink: 0,
                    '&:hover': {
                        backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                    },
                }}
                onClick={e => {
                    setIsModalOpen(true);
                }}
            >
                <TuneIcon />
            </MuiMaterials.Button>
            {renderModal()}
        </>
    );
}
export default SensorTemperatureGraphFilter;